import { WhiteboardState, StickyNote, BranchRelationship, InheritanceType, FileAttachment } from '../types';
import { TreeExportData } from './treeExporter';
import * as yaml from 'js-yaml';

import { API_ENDPOINTS } from '../config/api';
import { getAuth } from 'firebase/auth';

// Helper function to get authentication headers
async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      return {};
    }
    
    const token = await user.getIdToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  } catch (error) {
    console.error('Error getting auth headers:', error);
    return {};
  }
}

// Helper function to check if a file URL is likely expired
function isFileUrlExpired(uploadDate: Date): boolean {
  const now = new Date();
  const hoursSinceUpload = (now.getTime() - uploadDate.getTime()) / (1000 * 60 * 60);
  // URLs expire after 24 hours, so check if more than 23 hours have passed
  return hoursSinceUpload > 23;
}

// Helper function to validate and regenerate file URLs
async function validateAndRegenerateFileUrls(attachedFiles: FileAttachment[]): Promise<FileAttachment[]> {
  const validatedFiles: FileAttachment[] = [];
  
  for (const file of attachedFiles) {
    if (file.uploadStatus === 'completed') {
      // If file has a URL and fileId, check if URL is likely expired
      if (file.url && file.fileId) {
        const isExpired = isFileUrlExpired(file.uploadDate);
        
        if (!isExpired) {
          // URL is likely still valid, keep the file as is
  
          validatedFiles.push(file);
          continue;
        } else {
          // URL is likely expired, try to regenerate it
  
        }
      } else if (file.url && !file.fileId) {
        // File has URL but no fileId, keep as is (might be a direct URL)

        validatedFiles.push(file);
        continue;
      } else if (!file.url && file.fileId) {
        // File has fileId but no URL, try to regenerate

      } else {
        // File has neither URL nor fileId, mark as failed
        console.warn(`⚠️ File has no URL and no fileId: ${file.name}`);
        validatedFiles.push({
          ...file,
          uploadStatus: 'failed' as const,
          error: 'File has no URL and no fileId',
          url: undefined,
        });
        continue;
      }
      
      // Try to regenerate URL if we have a fileId
      if (file.fileId) {
        try {
  
          
          // Call the backend to get a new signed URL
          const response = await fetch(`${API_ENDPOINTS.FILES_GET}/${file.fileId}`, {
            headers: await getAuthHeaders(),
          });
          if (response.ok) {
            const fileData = await response.json();
            validatedFiles.push({
              ...file,
              url: fileData.downloadUrl,
            });

          } else if (response.status === 404) {
            console.warn(`⚠️ File not found on server: ${file.name} (${file.fileId})`);
            // Keep the file but mark it as failed
            validatedFiles.push({
              ...file,
              uploadStatus: 'failed' as const,
              error: 'File not found on server',
              url: undefined,
            });
          } else {
            console.warn(`⚠️ Could not regenerate URL for: ${file.name}, server returned ${response.status}`);
            // Keep the file but mark it as failed
            validatedFiles.push({
              ...file,
              uploadStatus: 'failed' as const,
              error: `Server error: ${response.status}`,
              url: undefined,
            });
          }
        } catch (error) {
          console.error(`❌ Error regenerating URL for ${file.name}:`, error);
          // Keep the file but mark it as failed
          validatedFiles.push({
            ...file,
            uploadStatus: 'failed' as const,
            error: 'Failed to regenerate file URL',
            url: undefined,
          });
        }
      }
    } else {
      // Keep files that are not completed (uploading, failed, etc.)
      // Don't change their status - preserve the original upload status
      validatedFiles.push(file);
    }
  }
  
  return validatedFiles;
}

export function detectFileFormat(content: string): 'json' | 'yaml' {
  const trimmed = content.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    return 'json';
  }
  return 'yaml';
}

export function parseFileContent(content: string): TreeExportData {
  try {
    // Try JSON first (faster, more reliable)
    return JSON.parse(content) as TreeExportData;
  } catch (jsonError) {
    try {
      // Fallback to YAML for backward compatibility
      return yaml.load(content) as TreeExportData;
    } catch (yamlError) {
      const jsonErrorMsg = jsonError instanceof Error ? jsonError.message : String(jsonError);
      const yamlErrorMsg = yamlError instanceof Error ? yamlError.message : String(yamlError);
      throw new Error(`Failed to parse file: JSON error: ${jsonErrorMsg}, YAML error: ${yamlErrorMsg}`);
    }
  }
}

export function parseYAML(yamlContent: string): TreeExportData {
  try {
    // Use js-yaml for reliable YAML parsing
    const result = yaml.load(yamlContent) as TreeExportData;
    return result;
  } catch (error) {
    throw new Error(`Failed to parse YAML: ${error}`);
  }
}



// Update importTreeFromYAML to use parseFileContent
export async function importTreeFromYAML(yamlContent: string, canvasId?: string): Promise<WhiteboardState> {
  const exportData = parseFileContent(yamlContent);
  
  // Validate the imported data
  if (!exportData.version || !exportData.notes || !exportData.canvas) {
    throw new Error('Invalid YAML format: missing required fields');
  }
  
  
  
  // Convert notes back to StickyNote format
  const notes: StickyNote[] = await Promise.all(exportData.notes.map(async (noteData) => {
    // Convert attached files and validate URLs
    const attachedFiles = noteData.attachedFiles?.map(file => ({
      id: file.id,
      name: file.name,
      fileId: file.fileId,
      size: file.size,
      type: file.type,
      uploadDate: new Date(file.uploadDate),
      uploadStatus: file.uploadStatus,
      url: file.url,
      error: file.error,
    })) || [];
    
    // Validate and regenerate file URLs if needed
    const validatedFiles = await validateAndRegenerateFileUrls(attachedFiles);
    
    
    
    if (attachedFiles.length > 0) {

      const failed = validatedFiles.filter(f => f.uploadStatus === 'failed').length;
      const uploading = validatedFiles.filter(f => f.uploadStatus === 'uploading').length;
      

      if (failed > 0) {
        console.warn(`⚠️ ${failed} files failed validation for note "${noteData.topic || 'Untitled'}"`);
        // Log details of failed files for debugging
        validatedFiles.filter(f => f.uploadStatus === 'failed').forEach(f => {
          console.warn(`  - ${f.name}: ${f.error}`);
        });
      }
      if (uploading > 0) {

      }
    }
    
    const finalNote = {
      id: noteData.id,
      content: noteData.content || '',
      position: noteData.position || { x: 0, y: 0 },
      color: noteData.color || '#F44336',
      zIndex: noteData.zIndex || Date.now(),
      width: noteData.width || 200,
      height: noteData.height || 200,
      parentId: noteData.parentId,
      level: noteData.level || 0,
      topic: noteData.topic || 'New Topic',
      status: noteData.status as 'root' | 'active' | 'merged' | 'archived' | undefined || 'root',
      showConverge: noteData.showConverge,
      showConvergeGlow: noteData.showConvergeGlow,
      mergeTargetId: noteData.mergeTargetId,
      chatHistory: noteData.chatHistory || [],
      mergeMessages: noteData.mergeMessages || [],
      copyMessages: noteData.copyMessages || [],
      systemPrompt: noteData.systemPrompt,
      persona: noteData.persona,
      hasCustomSystemPrompt: noteData.hasCustomSystemPrompt || false,
      firstMessageSent: noteData.firstMessageSent || false,
      needsPersonaSelection: noteData.needsPersonaSelection || false,
      inheritedSystemPrompt: noteData.inheritedSystemPrompt,
      attachedFiles: validatedFiles,
    };
    

    
    return finalNote;
  }));
  
  // Convert branch relationships back to BranchRelationship format
  const branchRelationships: BranchRelationship[] = (exportData.branchRelationships || []).map(relData => ({
    parentId: relData.parentId,
    branchPoint: relData.branchPoint || 0,
    branchId: relData.branchId,
    inheritanceType: relData.inheritanceType as InheritanceType || 'summary',
    createdAt: new Date(relData.createdAt || Date.now()),
  }));
  
  // Create the restored state
  const restoredState: WhiteboardState = {
    notes,
    selectedNoteIds: [],
    scale: exportData.canvas.scale || 0.7,
    offset: exportData.canvas.offset || { x: 0, y: 0 },
    isSelectionMode: false,
    selectionBox: null,
    branchRelationships,
    canvasName: exportData.canvasName,
    canvasId: canvasId,
  };
  

  
  return restoredState;
}

// New generic import function that works with both JSON and YAML
export async function importTreeFromFile(fileContent: string, canvasId?: string): Promise<WhiteboardState> {
  const exportData = parseFileContent(fileContent);
  
  // Validate the imported data
  if (!exportData.version || !exportData.notes || !exportData.canvas) {
    throw new Error('Invalid file format: missing required fields');
  }
  
  // Convert notes back to StickyNote format
  const notes: StickyNote[] = await Promise.all(exportData.notes.map(async (noteData) => {
    // Convert attached files and validate URLs
    const attachedFiles = noteData.attachedFiles?.map(file => ({
      id: file.id,
      name: file.name,
      fileId: file.fileId,
      size: file.size,
      type: file.type,
      uploadDate: new Date(file.uploadDate),
      uploadStatus: file.uploadStatus,
      url: file.url,
      error: file.error,
    })) || [];
    
    // Validate and regenerate file URLs if needed
    const validatedFiles = await validateAndRegenerateFileUrls(attachedFiles);
    
    if (attachedFiles.length > 0) {
      const failed = validatedFiles.filter(f => f.uploadStatus === 'failed').length;
      const uploading = validatedFiles.filter(f => f.uploadStatus === 'uploading').length;
      
      if (failed > 0) {
        console.warn(`⚠️ ${failed} files failed validation for note "${noteData.topic || 'Untitled'}"`);
        // Log details of failed files for debugging
        validatedFiles.filter(f => f.uploadStatus === 'failed').forEach(f => {
          console.warn(`  - ${f.name}: ${f.error}`);
        });
      }
      if (uploading > 0) {
        console.log(`📤 ${uploading} files still uploading for note "${noteData.topic || 'Untitled'}"`);
      }
    }
    
    const finalNote = {
      id: noteData.id,
      content: noteData.content || '',
      position: noteData.position || { x: 0, y: 0 },
      color: noteData.color || '#F44336',
      zIndex: noteData.zIndex || Date.now(),
      width: noteData.width || 200,
      height: noteData.height || 200,
      parentId: noteData.parentId,
      level: noteData.level || 0,
      topic: noteData.topic || 'New Topic',
      status: noteData.status as 'root' | 'active' | 'merged' | 'archived' | undefined || 'root',
      showConverge: noteData.showConverge,
      showConvergeGlow: noteData.showConvergeGlow,
      mergeTargetId: noteData.mergeTargetId,
      chatHistory: noteData.chatHistory || [],
      mergeMessages: noteData.mergeMessages || [],
      copyMessages: noteData.copyMessages || [],
      systemPrompt: noteData.systemPrompt,
      persona: noteData.persona,
      hasCustomSystemPrompt: noteData.hasCustomSystemPrompt || false,
      firstMessageSent: noteData.firstMessageSent || false,
      needsPersonaSelection: noteData.needsPersonaSelection || false,
      inheritedSystemPrompt: noteData.inheritedSystemPrompt,
      attachedFiles: validatedFiles,
    };
    
    return finalNote;
  }));
  
  // Convert branch relationships back to BranchRelationship format
  const branchRelationships: BranchRelationship[] = (exportData.branchRelationships || []).map(relData => ({
    parentId: relData.parentId,
    branchPoint: relData.branchPoint || 0,
    branchId: relData.branchId,
    inheritanceType: relData.inheritanceType as InheritanceType || 'summary',
    createdAt: new Date(relData.createdAt || Date.now()),
  }));
  
  // Create the restored state
  const restoredState: WhiteboardState = {
    notes,
    selectedNoteIds: [],
    scale: exportData.canvas.scale || 0.7,
    offset: exportData.canvas.offset || { x: 0, y: 0 },
    isSelectionMode: false,
    selectionBox: null,
    branchRelationships,
    canvasName: exportData.canvasName,
    canvasId: canvasId,
  };
  
  return restoredState;
}

export function loadYAMLFromFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.yaml,.yml';
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error('No file selected'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsText(file);
    };
    
    input.click();
  });
}

// New generic file loader that supports both JSON and YAML
export function loadTreeFromFile(): Promise<string> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,.yaml,.yml';
    
    input.onchange = (event) => {
      const file = (event.target as HTMLInputElement).files?.[0];
      if (!file) {
        reject(new Error('No file selected'));
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        resolve(content);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsText(file);
    };
    
    input.click();
  });
} 